import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { AlertTriangleIcon } from "lucide-react";
import { useId, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { linkStyles } from "@/components/ui/link";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import type { TSubjectDetail } from "@/lib/types";
import { SubjectForm } from "./SubjectForm";

export type SubjectEdit = Pick<
  TSubjectDetail,
  "id" | "caseId" | "dob" | "sex" | "ids"
>;

type ConflictReason = {
  subject: {
    id: number;
    caseId: string;
    createdAt: Date;
  };
};

export function SubjectEditDialog({
  subject,
  accession,
  ...props
}: {
  subject: SubjectEdit;
  accession?: { id: number };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const formId = useId();
  const [conflictReason, setConflictReason] = useState<
    ConflictReason | undefined
  >();
  const navigate = useNavigate();

  const update = trpc.subjects.update.useMutation({
    onSuccess: () => {
      props.onOpenChange?.(false);
      toast.success("Subject updated");
    },
    onError: (err) => {
      if (err.data?.code === "CONFLICT" && err.shape?.reason) {
        setConflictReason(err.shape.reason as ConflictReason);
      } else {
        toast.error("Error updating subject", {
          description: err.message,
        });
      }
    },
  });

  const move = trpc.subjects.moveAccessions.useMutation({
    onSuccess: (data) => {
      setConflictReason(undefined);
      toast.success(`Accession(s) moved to ${data.subject.caseId}`);
      if (data.providedAccessions) {
        props.onOpenChange?.(false);
      } else {
        navigate({ to: "/subjects/$id", params: { id: data.subject.id } });
      }
    },
    onError: (err) => {
      toast.error("Error moving accessions", {
        description: err.message,
      });
    },
  });

  return (
    <>
      <Modal isOpen={props.open} onOpenChange={props.onOpenChange}>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Edit Donor</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <SubjectForm
              formId={formId}
              subject={subject}
              onSave={(values) => {
                if ("id" in values) {
                  update.mutate(values);
                } else {
                  console.error("Invalid values for update, missing id");
                }
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              type="submit"
              form={formId}
              isPending={update.isPending}
              isDisabled={update.isPending}
            >
              Save
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
      {conflictReason && (
        <Modal
          isOpen={!!conflictReason}
          onOpenChange={(flag) => {
            if (!flag) {
              setConflictReason(undefined);
            }
          }}
        >
          <Dialog>
            <DialogHeader>
              <DialogTitle className="flex gap-2 items-center">
                <AlertTriangleIcon className="size-4 text-yellow-600" />
                Donor conflict
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Text className="mb-4">
                Another donor already exists with the same identifier. This
                donor cannot be modified to have the same identifier.
              </Text>
              <Text className="mb-4">
                The donor with ID{" "}
                <a
                  href={`/subjects/${conflictReason.subject.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkStyles({ intent: "primary" })}
                >
                  {conflictReason.subject.caseId}
                </a>{" "}
                was created on{" "}
                {dayjs(conflictReason.subject.createdAt).format("YYYY-MM-DD")}{" "}
                at {dayjs(conflictReason.subject.createdAt).format("HH:mm")}.
              </Text>
              {accession ? (
                <Text>
                  Would you like to move accession {accession.id} to donor with
                  ID: {conflictReason.subject.caseId}?
                </Text>
              ) : (
                <Text>
                  Would you like to add this donor's accessions to the existing
                  donor?
                </Text>
              )}
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                onPress={() => {
                  move.mutate({
                    accessions: accession ? [accession.id] : undefined,
                    fromSubjectId: subject.id,
                    toSubjectId: conflictReason.subject.id,
                  });
                }}
                isPending={move.isPending}
                isDisabled={move.isPending}
              >
                Add accession{accession ? "" : "s"}
              </Button>
              <DialogCloseButton>Go back</DialogCloseButton>
            </DialogFooter>
          </Dialog>
        </Modal>
      )}
    </>
  );
}
